/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */
import React, {
    useEffect, useRef, useState,
} from 'react';
import {
    string, array, shape, object,
} from 'prop-types';
import mbpUtil from '@api-util/mbp';
import mbpLogger from '@module-logger/mbp';
import { useSelector, useDispatch } from 'react-redux';
import { selectedSkuType } from '../common/types';
import { getAccessToken } from '../../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getBrandName } from '../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getSSRDeviceType, getPageType } from '../../../../../../state/ducks/App/App-Selectors';
import useSalesforcePersonalizedContentQuery from '../../../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import useClickStreamCustomEventsTracking from '../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import RenderWhenVisible from '../../../../../helpers/componentVisibility/RenderWhenVisible';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { emitCustomTrackingEvent } from '../../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import getTrustedOrigin from '../../../../ConversationalUI/helper/TrustedOrigin';

const QuestionsAndAnswersAI = ({
    product, featureFlags, selectedSku,
}) => {
    const iframeRef = useRef(null);
    const [isIframeLoaded, setIsIframeLoaded] = useState(true);
    const accessToken = useSelector(getAccessToken);
    const ssrDeviceType = useSelector(getSSRDeviceType);
    const pageType = useSelector(getPageType);
    const brandName = useSelector(getBrandName);
    const dispatch = useDispatch();
    const testEnabled = featureFlags['ai-questions-and-answers-pdp'];
    const abTestName = 'PDP Q and A';
    const abInteractionName = `${brandName} - Get Campaign - ${abTestName}`;
    const trustedOrigin = getTrustedOrigin(mbpUtil?.getEnv('APP_CONVERSATIONAL_UI_HOST'));
    const sfData = useRef();
    // State to hold the dynamic height of the iframe
    const [iframeHeight, setIframeHeight] = useState(0);

    const {
        data: questionsAndAnswersComparisonData, error: err, loading, variables,
    } = useSalesforcePersonalizedContentQuery({
        queryName: 'qAndAABTest',
        interactionName: abInteractionName,
        featureFlag: testEnabled,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });

    useEffect(() => {
        if (questionsAndAnswersComparisonData?.contentSF) {
            sfData.current = questionsAndAnswersComparisonData?.contentSF;
        }
    }, [questionsAndAnswersComparisonData?.contentSF]);

    useClickStreamCustomEventsTracking({
        salesforceResponse: questionsAndAnswersComparisonData?.contentSF,
        isFireImpression: true,
        page: { type: pageType },
    });

    useEffect(() => {
        if (accessToken) {
            try {
                const iframe = iframeRef.current;
                if (iframe && iframe?.contentWindow) {
                    iframe.contentWindow.postMessage(
                        { type: 'q_and_a-token-update', data: accessToken },
                        `${trustedOrigin}/chat/q_and_a`,
                    );
                }
            } catch (e) {
                mbpLogger.logError({
                    function: 'useEffect -> accessToken',
                    module: 'QuestionsAndAnswerAI.js',
                    jsError: e,
                    message: 'Failed to post updated token.',
                });
            }
        }
    }, [accessToken]);

    useEffect(() => {
        const handleMessage = async (event) => {
            try {
                if (event.data.type && typeof event.data.type === 'string' && event.data.type.includes('q_and_a')) {
                    const iframe = iframeRef.current;
                    const auth = JSON.parse(JSON.parse(sessionStorage.getItem('persist:member'))?.auth);
                    const profile = JSON.parse(JSON.parse(sessionStorage.getItem('persist:member'))?.profile);
                    const brand_abbreviation = JSON.parse(JSON.parse(sessionStorage.getItem('persist:ui'))?.brandId);
                    const browser_uuid = localStorage?.getItem('browserUUID') || '';
                    const ssr_device_type = ssrDeviceType;
                    if (event.data.type === 'q_and_a-iframeHeight' && typeof event.data.height === 'number') {
                        // Check if the message is for height adjustment and if data exists
                        const newHeight = event.data.height;
                        setIframeHeight(newHeight);
                    } else if (event.data.type === 'trigger-q_and_a-session-info') {
                        iframe.contentWindow.postMessage({
                            type: 'q_and_a-session-info',
                            data: {
                                ssr_device_type,
                                brand_abbreviation,
                                access_token: auth?.accessToken,
                                product_sku: product.partNumber,
                                selected_child_sku: selectedSku.name,
                                email: profile?.email,
                                browser_uuid,
                            },
                        }, '*');
                    } else if (event.data.type === 'trigger-q_and_a-token-update') {
                        iframe.contentWindow.postMessage({ type: 'q_and_a-token-update', data: auth?.accessToken }, '*');
                    } else if (event.data.type === 'trigger-q_and_a-event_tracking' && event.data.tracking_data) {
                        const trackingData = event.data.tracking_data;
                        const clickstreamData = event.data.clickstream_data;
                        if (clickstreamData?.action === 'Click') {
                            // handle clickstream data for clickstream reporting
                            dispatch(emitCustomTrackingEvent({
                                action: clickstreamData?.action,
                                salesforceResponse: sfData.current,
                                page: { type: pageType },
                                label: clickstreamData?.label,
                                category: clickstreamData?.category,
                                eventSubType: 'campaign-tracking',
                            }));
                        }
                        if (trackingData) {
                            // handle tracking data for GA4 reporting
                            dispatch(trackEvent(trackingData));
                        }
                    }
                }
            } catch (error) {
                mbpLogger.logError({
                    appName: process.env.npm_package_name,
                    errorMessage: error,
                    message: `Q&A AI: Could not post value - ERROR: ${error}`,
                });
            }
        };
        if (isIframeLoaded) {
            // Keep the old reporting style so as to not mess with that reporting
            dispatch(trackEvent({
                eventCategory: 'pdp',
                eventAction: 'New',
                eventLabel: `Impression ${abTestName}`,
            }));
            // report new event as well to rectify reporting gap with variant and control
            dispatch(trackEvent({
                eventCategory: 'pdp',
                eventAction: 'Variant',
                eventLabel: `${abTestName} Impression `,
            }));
            window?.addEventListener('message', handleMessage);
        }
        return () => {
            window?.removeEventListener('message', handleMessage);
        };
    }, [isIframeLoaded, trustedOrigin]);

    if (testEnabled && (loading || variables?.skip)) {
        // if we are still loading, do not show anything at all yet
        return null;
    }

    if (testEnabled && !loading && (err || variables?.eidError)) {
        // if there was an error, log the error and continue on where we will show the control
        mbpLogger.logError({
            function: abInteractionName,
            message: 'Error loading API data',
            appName: process.env.npm_package_name,
            module: 'QuestionsAndAnswersAI',
            jsError: err,
        });
    }

    const questionsAndAnswersComparisonDataUserGroup = questionsAndAnswersComparisonData?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;
    if (
        typeof window !== 'undefined'
        && questionsAndAnswersComparisonDataUserGroup === 'Test'
        && mbpUtil?.getEnv('APP_CONVERSATIONAL_UI_HOST')
        && isIframeLoaded
    ) {
        return (
            <iframe
                id="q_and_a"
                ref={iframeRef}
                title="q_and_a"
                className="q_and_a"
                allowFullScreen
                style={{
                    width: '100%',
                    border: 'none',
                    minHeight: '100px',
                    height: `${iframeHeight}px`, // Dynamic height from state
                    overflow: 'hidden', // Hide internal scrollbars if content overflows initially
                }}
                src={`${trustedOrigin}/chat/q_and_a`}
                onLoad={() => setIsIframeLoaded(true)}
                onError={() => setIsIframeLoaded(false)}
            />
        );
    }

    // if we get here and the test is enabled, that means the test is on, but rendering control
    // report our control event to GA4
    if (testEnabled) {
        // report new event for rendering control (nothing) to rectify reporting gap
        dispatch(trackEvent({
            eventCategory: 'pdp',
            eventAction: 'Control',
            eventLabel: `${abTestName} Impression`,
        }));
    }
    return null;
};

const QuestionsAndAnswersAIWrapper = ({
    product, featureFlags, selectedSku,
}) => (
    <RenderWhenVisible>
        <QuestionsAndAnswersAI product={product} featureFlags={featureFlags} selectedSku={selectedSku} />
    </RenderWhenVisible>
);

const componentPropTypes = {
    product: shape({
        shortDescription: string.isRequired,
        longDescription: string.isRequired,
        productContents: string.isRequired,
        productSkus: array.isRequired,
        partNumber: string.isRequired,
        brand: string.isRequired,
    }).isRequired,
    featureFlags: array.isRequired,
    selectedSku: selectedSkuType.isRequired,
};
QuestionsAndAnswersAI.propTypes = componentPropTypes;
QuestionsAndAnswersAIWrapper.propTypes = componentPropTypes;

export default QuestionsAndAnswersAIWrapper;
